import { SomeJSONSchema } from "ajv/dist/types/json-schema";
import { FormBlock } from "./FormTypes";

export const EmptyObjectSchema: SomeJSONSchema = {
  type: "object",
  nullable: true,
  required: [],
};

export const DateParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    schema: {
      type: "object",
      properties: {
        type: { type: "string", pattern: "string" },
        minLength: { type: "integer" },
        maxLength: { type: "integer" },
        pattern: { type: "string" },
      },
      required: ["type"],
    },
    formatString: {
      type: "string",
    },
  },
  required: ["schema"],
};

export const ImageParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    img_src: {
      type: "string",
      minLength: 3,
    },
  },
  required: ["img_src"],
};

export const FileUploadParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    allowed_file_types: {
      type: "array",
      items: {
        type: "string",
      },
    },
    max_file_bytes: {
      type: "integer",
    },
    max_file_count: {
      type: "integer",
    },
  },
  required: ["allowed_file_types", "max_file_bytes", "max_file_count"],
};

export const LegalParamsSchema: SomeJSONSchema = {
  type: "object",
  required: [],
};

export const CheckboxParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    schema: {
      type: "object",
      properties: {
        type: { type: "string", pattern: "array" },
        items: {
          type: "object",
          properties: {
            type: { type: "string", pattern: "string" },
            enum: { type: "array", items: { type: "string" }, minItems: 1 },
          },
          required: ["type"],
        },
      },
      required: ["items"],
    },
  },
  required: ["schema"],
};

export const PhoneParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    default_country: {
      type: "string",
    },
    schema: {
      type: "object",
      properties: {
        type: { type: "string", pattern: "string" },
        minLength: { type: "integer" },
        maxLength: { type: "integer" },
        pattern: { type: "string" },
      },
      required: [],
    },
  },
  required: ["schema"],
};

const OldMultiChoiceParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    schema: {
      type: "object",
      properties: {
        type: { type: "string", pattern: "string" },
        enum: { type: "array", items: { type: "string" }, minItems: 1 },
      },
      required: ["type", "enum"],
    },
    allow_other: {
      type: "boolean",
    },
  },
  required: ["schema"],
};

const NewMultiChoiceParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    options: {
      type: "array",
      items: {
        type: "string",
      },
      minItems: 1,
    },
    schema: {
      type: "object",
      properties: {
        anyOf: {
          type: "array",
          items: {
            type: "object",
            properties: {
              type: { type: "string" },
              enum: { type: "array", items: { type: "string" }, minItems: 1 },
            },
            required: ["type"],
          },
        },
      },
      required: ["anyOf"],
    },
    allow_other: {
      type: "boolean",
    },
  },
  required: ["options", "schema"],
};

export const MultiChoiceParamsSchema: SomeJSONSchema = {
  oneOf: [OldMultiChoiceParamsSchema, NewMultiChoiceParamsSchema],
};

export const SelectParamsSchema: SomeJSONSchema = {
  ...MultiChoiceParamsSchema,
};

export const RangeParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    schema: {
      type: "object",
      properties: {
        type: { type: "string", pattern: "integer" },
        minimum: { type: "integer" },
        // The type cast below is needed because `$data` is accepted by AJV, but not part of the official JSON schema spec :/
        maximum: {
          type: "integer",
          exclusiveMinimum: { $data: "1/minimum" } as unknown as number,
        },
      },
      required: ["type", "minimum", "maximum"],
    },
    min_label: { type: "string" },
    max_label: { type: "string" },
    median_label: { type: "string" },
  },
  required: ["schema"],
};

export const RankingParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    schema: {
      type: "object",
      properties: {
        type: { type: "string", pattern: "array" },
        minItems: { type: "integer" },
        uniqueItems: { type: "boolean" },
        items: {
          type: "object",
          properties: {
            type: { type: "string", pattern: "string" },
            pattern: { type: "string" },
          },
          required: ["type"],
        },
      },
      required: ["type"],
    },
    labels: {
      type: "array",
      items: {
        type: "string",
      },
    },
  },
  required: ["schema", "labels"],
};

export const StarRatingParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    schema: {
      type: "object",
      properties: {
        type: { type: "string", pattern: "integer" },
        minimum: { type: "integer" },
        // The type cast below is needed because `$data` is accepted by AJV, but not part of the official JSON schema spec :/
        maximum: {
          type: "integer",
          exclusiveMinimum: { $data: "1/minimum" } as unknown as number,
        },
      },
      required: ["type", "maximum"],
    },
    rating_type: { type: "string" }, // TODO: more rating types
  },
  required: ["schema"],
};

export const TextAreaParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    schema: {
      type: "object",
      properties: {
        type: { type: "string", pattern: "string" },
        minLength: { type: "integer" },
        maxLength: { type: "integer" },
      },
      required: ["type"],
    },
  },
  required: ["schema"],
};

export const TextInputParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    schema: {
      type: "object",
      properties: {
        type: { type: "string", pattern: "string" },
        minLength: { type: "integer" },
        maxLength: { type: "integer" },
        format: { type: "string" },
      },
      required: ["type"],
    },
    formatString: {
      type: "string",
    },
  },
  required: ["schema"],
};

export const NumberInputParamsSchema: SomeJSONSchema = {
  type: "object",
  properties: {
    schema: {
      type: "object",
      properties: {
        type: { type: "string", pattern: "integer" },
        minimum: { type: "integer" },
        // The type cast below is needed because `$data` is accepted by AJV, but not part of the official JSON schema spec :/
        maximum: {
          type: "integer",
          exclusiveMinimum: { $data: "1/minimum" } as unknown as number,
        },
      },
      required: ["type"],
    },
    formatString: {
      type: "string",
    },
  },
  required: ["schema"],
};

export const ComponentMetaSchema: Record<
  FormBlock["block_type"],
  SomeJSONSchema
> = {
  checkboxes: CheckboxParamsSchema,
  date: DateParamsSchema,
  date_of_birth: DateParamsSchema,
  dropdown: SelectParamsSchema,
  multi_choice: MultiChoiceParamsSchema,
  email: TextInputParamsSchema,
  first_name: TextInputParamsSchema,
  last_name: TextInputParamsSchema,
  long_text: TextAreaParamsSchema,
  legal: LegalParamsSchema,
  image: ImageParamsSchema,
  nps: RangeParamsSchema,
  scale: RangeParamsSchema,
  star_rating: StarRatingParamsSchema,
  number: NumberInputParamsSchema,
  short_text: TextInputParamsSchema,
  yes_no: MultiChoiceParamsSchema,
  ranking: RankingParamsSchema,
  phone: PhoneParamsSchema,
  text_content: EmptyObjectSchema,
  video: EmptyObjectSchema,
  file_upload: FileUploadParamsSchema,
};
